import type {
	Fragment,
	Node as PMNode,
	NodeType,
	ResolvedPos,
} from '@atlaskit/editor-prosemirror/model';
import { Slice } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { findWrapping } from '@atlaskit/editor-prosemirror/transform';
import type { Transform } from '@atlaskit/editor-prosemirror/transform';

import type { ToolCallArguments } from '../../../aiSuggestionsPluginType';

function wrapChild(
	fragment: Fragment,
	type: NodeType,
	attrs: Record<string, unknown>,
): PMNode | null {
	if (!fragment.firstChild) {
		return null;
	}

	const wrappingNodeTypes = type.contentMatch.findWrapping(fragment.firstChild.type);
	let currentChildNodes: readonly PMNode[] | null = null;
	if (wrappingNodeTypes) {
		currentChildNodes = fragment.content;
		for (let i = wrappingNodeTypes.length - 1; i >= 0; i--) {
			const wrappingNodeType = wrappingNodeTypes[i];
			const nextChildNode = wrappingNodeType.createAndFill(null, currentChildNodes);
			if (nextChildNode) {
				currentChildNodes = [nextChildNode];
			} else {
				currentChildNodes = null;
				break;
			}
		}
	}
	if (currentChildNodes) {
		return type.createAndFill(attrs, currentChildNodes);
	}

	return null;
}

function findPosForNode(node: PMNode, $to: ResolvedPos): number | null {
	for (let depth = $to.depth; depth >= 0; depth--) {
		const ancestor = $to.node(depth);
		if (ancestor.type.contentMatch.matchType(node.type)) {
			return $to.after(depth + 1);
		}
	}
	return null;
}

export const wrapNode = ({
	toolCallArguments,
	tr,
	pos,
}: {
	pos: number;
	toolCallArguments: ToolCallArguments<'wrap' | 'wrapNode'>;
	tr: Transaction | Transform;
}): Transaction | Transform => {
	const $from = tr.doc.resolve(pos);
	const node = tr.doc.nodeAt(pos);
	if (!node) {
		return tr;
	}
	const $to = tr.doc.resolve(pos + node.nodeSize);
	const range = $from.blockRange($to);
	if (range) {
		const type = tr.doc.type.schema.nodes[toolCallArguments.wrapperType];
		const attrs = toolCallArguments.wrapperAttrs;
		try {
			const wrapping = findWrapping(range, type, attrs);
			if (wrapping !== null) {
				tr.wrap(range, wrapping);
			} else {
				const slice = tr.doc.slice(range.$from.pos, range.$to.pos);
				const fragment = slice.content;
				let node = type.createAndFill(attrs, fragment);
				if (!node) {
					node = wrapChild(fragment, type, attrs);
				}
				if (node) {
					const insertPos = findPosForNode(node, $to);
					if (insertPos !== null) {
						tr.insert(insertPos, node);
						tr.replaceRange(range.$from.pos, range.$to.pos, Slice.empty);
					}
				}
			}
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(e);
		}
	}
	return tr;
};
